import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Table, TableHead, TableBody, TableRow,
    TableCell, TextField, Chip, Button, CircularProgress, Paper, Alert
} from '@mui/material';
import { TaxService } from '../../services/tax.service';
import { ITaxExemption } from '../../interfaces/tax.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

const TaxExemptionList: React.FC = () => {
    const { t } = useTranslation(['tax', 'common']);
    const { showSuccess, showError } = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [exemptions, setExemptions] = useState<ITaxExemption[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        TaxService.getExemptions(institutionId)
            .then(setExemptions)
            .catch(() => showError(t('exemptions.toast.loadFailed')))
            .finally(() => setLoading(false));
    }, [institutionId]);

    const updateCap = (idx: number, value: string) => {
        setExemptions(prev => prev.map((e, i) =>
            i === idx ? { ...e, monthlyExemptionCap: parseFloat(value) || 0 } : e));
    };

    const save = async () => {
        setSaving(true);
        try {
            const saved = await TaxService.saveExemptions(exemptions, institutionId);
            setExemptions(saved);
            showSuccess(t('exemptions.toast.saved'));
        } catch {
            showError(t('exemptions.toast.saveFailed'));
        } finally { setSaving(false); }
    };

    if (loading) return <CircularProgress />;

    return (
        <Box p={3}>
            <Typography variant="h5" mb={1}>{t('exemptions.title')}</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
                {t('exemptions.intro')}
            </Typography>

            <Alert severity="info" sx={{ mb: 2 }}>
                {t('exemptions.info')}
            </Alert>

            <Paper>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ background: '#f1f5f9' }}>
                            <TableCell>{t('exemptions.columns.component')}</TableCell>
                            <TableCell>{t('common:fields.type')}</TableCell>
                            <TableCell>{t('exemptions.columns.cap')}</TableCell>
                            <TableCell>{t('common:fields.status')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {exemptions.map((ex, idx) => (
                            <TableRow key={ex.salaryComponentId}>
                                <TableCell>{ex.componentName}</TableCell>
                                <TableCell>
                                    <Chip label={t('shared.earning')} color="success" size="small" />
                                </TableCell>
                                <TableCell>
                                    <TextField
                                        size="small" type="number"
                                        value={ex.monthlyExemptionCap}
                                        onChange={e => updateCap(idx, e.target.value)}
                                        inputProps={{ min: 0 }}
                                        sx={{ width: 120 }}
                                    />
                                </TableCell>
                                <TableCell>
                                    {ex.monthlyExemptionCap > 0
                                        ? <Chip label={t('exemptions.partiallyExempt')} color="success" size="small" />
                                        : <Chip label={t('exemptions.fullyTaxable')} color="error" size="small" />}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <Box p={2}>
                    <Button variant="contained" onClick={save} disabled={saving}>
                        {saving ? t('shared.saving') : t('exemptions.save')}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default TaxExemptionList;
